// The scan entry control every floor screen shares (spec 06 §3.5, §9.15).
//
// Three input paths, in the order a warehouse actually uses them:
//   1. KEYBOARD WEDGE — the ring/gun scanners KazKabel already owns type the
//      decoded payload into the focused field and press Enter. This is the
//      default and needs no permission, no camera and no battery. A screen that
//      passes `captureWedge` claims that burst NO MATTER WHICH FIELD IT LANDED
//      IN (see the effect below) and restores the field it landed in.
//   2. CAMERA — a phone with `BarcodeDetector`. Progressive enhancement: the
//      button appears only when the API and a camera are actually present, and
//      says why when they are not. A camera button that opens a black rectangle
//      is worse than no camera button.
//   3. MANUAL SERIAL — §9.15's damaged-label path. Serials are short and the
//      field is always visible; there is no "switch to manual" mode to find.
//
// The payload is passed to the server VERBATIM. Resolution is server-side and
// tenant-scoped (§9.9: the subdomain printed on a label is display only and is
// never trusted), so this component deliberately does no parsing — a client
// that decided what a QR "meant" would be a second, weaker resolver, and one
// that could leak the existence of another tenant's id.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * A keyboard wedge types its payload as fast as the OS can deliver keystrokes
 * and terminates with Enter. Nothing a human types comes close, so a burst of
 * characters at more than this rate is a scanner, not a person.
 */
const WEDGE_MAX_GAP_MS = 35;
const WEDGE_MIN_LENGTH = 4;

function isEditable(element: Element | null): boolean {
  if (element === null) return false;
  const tag = element.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (element as HTMLInputElement).type;
    return type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit";
  }
  return (element as HTMLElement).isContentEditable;
}

/**
 * The marker every `ScanInput` payload box carries. A burst fired while ANOTHER
 * scan box has focus is aimed at that box — the operator picked it — so a
 * wedge-claiming panel must leave it alone. Some screens carry two scan boxes
 * (op-run's material scan beside the serial scan) and only one of them claims
 * stray input; without this the claiming one would steal bursts the operator
 * deliberately aimed at its neighbour.
 */
const SCAN_FIELD_ATTR = "data-floor-scan-field";

function isForeignScanField(element: Element | null, own: HTMLInputElement | null): boolean {
  return element !== null && element !== own && element.hasAttribute(SCAN_FIELD_ATTR);
}

/** What the burst's first characters overwrote, so the claim can put it back. */
interface WedgeVictim {
  readonly element: HTMLInputElement | HTMLTextAreaElement;
  readonly value: string;
  readonly selectionStart: number | null;
  readonly selectionEnd: number | null;
}

function victimOf(element: Element | null): WedgeVictim | null {
  if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) return null;
  // `selectionStart` throws on number/email inputs in some engines — a field we
  // cannot read a caret from is still restorable by value alone.
  let selectionStart: number | null = null;
  let selectionEnd: number | null = null;
  try {
    selectionStart = element.selectionStart;
    selectionEnd = element.selectionEnd;
  } catch {
    /* caret unavailable on this input type */
  }
  return { element, value: element.value, selectionStart, selectionEnd };
}

/**
 * Put a claimed burst's victim back exactly as the operator left it.
 *
 * React owns these inputs, so the native value setter is called and an `input`
 * event dispatched: assigning `.value` alone would be silently overwritten on
 * the next render, which is the failure mode "we cleared it" would hide.
 */
function restoreVictim(victim: WedgeVictim): void {
  const proto =
    victim.element instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  // Bound to the victim at extraction — never a floating method reference.
  const setValue = Object.getOwnPropertyDescriptor(proto, "value")?.set?.bind(victim.element);
  if (setValue === undefined) victim.element.value = victim.value;
  else setValue(victim.value);
  victim.element.dispatchEvent(new Event("input", { bubbles: true }));
  try {
    victim.element.setSelectionRange(
      victim.selectionStart ?? victim.value.length,
      victim.selectionEnd ?? victim.value.length,
    );
  } catch {
    /* caret unavailable on this input type */
  }
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

function barcodeDetectorCtor(): BarcodeDetectorCtor | undefined {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  return typeof ctor === "function" ? ctor : undefined;
}

function cameraAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    barcodeDetectorCtor() !== undefined
  );
}

export function ScanInput({
  onScan,
  label,
  placeholder,
  autoFocus = true,
  busy = false,
  /**
   * Route stray wedge input here. Exactly ONE scan input per screen may claim
   * it — two would each fire on the same burst and double-scan a bobbin.
   */
  captureWedge = false,
}: {
  onScan: (payload: string) => void;
  label: string;
  placeholder: string;
  autoFocus?: boolean;
  busy?: boolean;
  captureWedge?: boolean;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const [value, setValue] = useState("");
  const [camera, setCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | undefined>(undefined);
  const canScan = cameraAvailable();

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  /**
   * WEDGE CAPTURE. The ring and gun scanners KazKabel already owns type into
   * whatever happens to be focused, and every floor screen has other text
   * fields next to the scan box. An operator who taps "Measured length" and
   * then pulls the trigger used to get the barcode IN THE MEASURE BOX — a
   * length field reading `0.019` because that is what a QR's digits look like
   * after the slashes are dropped, or a raw `https://…/q/su/…` submitted as an
   * engraved serial and recorded as a lineage row nothing can resolve.
   *
   * CORRECTED 2026-08-06 (the inversion this component's own comment used to
   * argue against). The old rule was "claim the burst unless the operator is
   * focused in an editable field, because the keystrokes might be theirs".
   * That rule reads the FOCUS to decide authorship, and focus is exactly what a
   * wedge ignores — the scanner fires into whatever the thumb last touched.
   * SPEED is what distinguishes a machine from a person: nothing human sustains
   * sub-35 ms gaps over four or more characters, which is the same threshold
   * this file already trusted when nothing had focus. So the burst is claimed
   * from ANY field, and the field it landed in is put back exactly as the
   * operator left it (`restoreVictim`) before the payload is dispatched.
   *
   * Two things are deliberately NOT claimed:
   *  * a burst while ANOTHER scan box has focus (`isForeignScanField`) — the
   *    operator aimed at that box, and op-run carries two;
   *  * a burst into THIS field — the form's own submit already owns it.
   *
   * Characters are never swallowed mid-burst: they land, and the restore
   * happens on the terminating Enter. A human typist therefore cannot lose
   * keystrokes to a burst that never qualifies — the field simply keeps what
   * was typed, which is the failure mode of last resort rather than a silent
   * deletion.
   */
  useEffect(() => {
    if (!captureWedge || typeof document === "undefined") return;
    let buffer = "";
    let last = 0;
    let victim: WedgeVictim | null = null;
    const onKeyDown = (event: KeyboardEvent): void => {
      const active = document.activeElement;
      const own = active === fieldRef.current;
      if (isForeignScanField(active, fieldRef.current)) {
        buffer = "";
        victim = null;
        return;
      }
      const now = Date.now();
      if (event.key === "Enter") {
        const payload = buffer.trim();
        const overwritten = victim;
        buffer = "";
        victim = null;
        if (payload.length >= WEDGE_MIN_LENGTH && !own) {
          // Capture phase + stopPropagation: the victim field's own Enter
          // handler must never see a scanner's terminator, or a barcode-laden
          // quantity box would submit itself before this could put it back.
          event.preventDefault();
          event.stopPropagation();
          if (overwritten !== null) restoreVictim(overwritten);
          onScanRef.current(payload);
        }
        return;
      }
      if (event.key.length !== 1) return;
      // A HELD KEY IS NOT A SCANNER. OS auto-repeat can beat the gap threshold,
      // and a glove resting on a digit would otherwise "scan" `1111` and blank
      // the field it was resting on. Repeats never buffer; they still type.
      if (event.repeat) return;
      if (now - last > WEDGE_MAX_GAP_MS) {
        // A new burst starts here. Snapshot BEFORE the keystroke's default
        // insertion runs, so the value recorded is the operator's own.
        buffer = "";
        victim = own ? null : victimOf(isEditable(active) ? active : null);
      }
      last = now;
      buffer += event.key;
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [captureWedge]);

  const stopCamera = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = undefined;
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  useEffect(() => {
    if (!camera) {
      stopCamera();
      return;
    }
    let cancelled = false;
    let raf = 0;
    const Detector = barcodeDetectorCtor();
    if (Detector === undefined) {
      setCamera(false);
      return;
    }
    const detector = new Detector({ formats: ["qr_code", "code_128", "code_39"] });

    const run = async (): Promise<void> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video === null) return;
        video.srcObject = stream;
        await video.play();
        const tick = (): void => {
          if (cancelled) return;
          void detector
            .detect(video)
            .then((codes) => {
              const first = codes[0];
              if (first !== undefined && first.rawValue.length > 0) {
                cancelled = true;
                setCamera(false);
                onScan(first.rawValue);
                return;
              }
              raf = requestAnimationFrame(tick);
            })
            .catch(() => {
              raf = requestAnimationFrame(tick);
            });
        };
        raf = requestAnimationFrame(tick);
      } catch {
        if (!cancelled) {
          setCameraError(t("inventory.floor.scan.cameraDenied"));
          setCamera(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stopCamera();
    };
  }, [camera, onScan, stopCamera, t]);

  return (
    <div className="floor-scaninput">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const payload = value.trim();
          if (payload.length === 0) return;
          setValue("");
          onScan(payload);
          // Re-arm for the next bobbin without a tap. Twenty items off a truck
          // is twenty scans, and re-focusing by hand between each one is where
          // the operator gives up and writes on paper.
          fieldRef.current?.focus();
        }}
      >
        <label htmlFor={inputId}>{label}</label>
        <div className="floor-scaninput-row">
          <input
            id={inputId}
            ref={fieldRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus={autoFocus}
            value={value}
            placeholder={placeholder}
            onChange={(event) => {
              setValue(event.target.value);
            }}
            data-testid="floor-scan-payload"
            /* The marker a wedge-claiming panel checks before stealing a burst:
               a scan box is where a scan BELONGS, so its neighbour leaves it
               be. Kept in sync with SCAN_FIELD_ATTR by the component's own
               test, which asserts the attribute is present on the field. */
            data-floor-scan-field="true"
          />
          <button
            type="submit"
            className="floor-btn floor-btn-primary floor-scaninput-go"
            disabled={busy || value.trim().length === 0}
            data-testid="floor-scan-submit"
          >
            {t("inventory.floor.scan.go")}
          </button>
        </div>
      </form>

      {canScan ? (
        <button
          type="button"
          className="floor-btn floor-btn-ghost"
          onClick={() => {
            setCameraError(undefined);
            setCamera((on) => !on);
          }}
          data-testid="floor-scan-camera"
        >
          {camera ? t("inventory.floor.scan.cameraStop") : t("inventory.floor.scan.cameraStart")}
        </button>
      ) : (
        <p className="floor-hint" data-testid="floor-scan-nocamera">
          {t("inventory.floor.scan.cameraUnavailable")}
        </p>
      )}

      {cameraError !== undefined ? (
        <p className="floor-scan-error" role="alert">
          {cameraError}
        </p>
      ) : null}

      {camera ? (
        <video
          ref={videoRef}
          className="floor-scan-video"
          muted
          playsInline
          data-testid="floor-scan-video"
        />
      ) : null}
    </div>
  );
}
